import { Box, Modal, Pagination } from '@mui/material';
import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { RiDeleteBin6Fill, RiEdit2Fill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from "yup";
import { createTerms, deleteTerms, getAllTerms, updateTerms } from '../Redux/Slice/TermsCondition.slice';
import { motion } from 'framer-motion';
import { FaPlus, FaSearch, FaTimes, FaGavel } from 'react-icons/fa';

export default function TermsConditions() {
    const [searchValue, setSearchValue] = useState('');
    const dispatch = useDispatch();
    const Terms = useSelector(state => state.term.Terms);
    const loading = useSelector(state => state.category.loading);
    const [createopen, setCreateopen] = useState(false);
    const [delOpen, setDelOpen] = useState(false);
    const [TermsData, setTermsData] = useState("");

    const validationSchema = Yup.object({
        title: Yup.string().required("Title is required"),
        description: Yup.string().required("Description is required"),
    });

    const formik = useFormik({
        initialValues: { title: "", description: "" },
        validationSchema,
        onSubmit: async (values) => {
            if (TermsData) dispatch(updateTerms({ _id: TermsData._id, values }));
            else dispatch(createTerms(values));
            handleCreateClose();
        },
    });

    useEffect(() => {
        dispatch(getAllTerms())
    }, [dispatch])

    const filteredData = Terms.filter(data => {
        const titleMatch = data?.title?.toLowerCase().includes(searchValue.toLowerCase());
        const descriptionText = data?.description?.replace(/<[^>]*>/g, '')?.toLowerCase();
        const descriptionMatch = descriptionText?.includes(searchValue?.toLowerCase());
        return titleMatch || descriptionMatch;
    });

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleOpen = (data) => {
        setCreateopen(true);
        setTermsData(data);
        if (data) {
            formik.setValues({
                title: data.title,
                description: data.description
            });
        } else {
            formik.setValues({ title: '', description: '' });
        }
    };

    const handleCreateClose = () => {
        setCreateopen(false);
        setTermsData("");
        formik.resetForm();
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Terms & Conditions</h1>
                    <p className="text-gray-400">Define the rules and agreements for your platform users.</p>
                </div>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => handleOpen(null)} className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all">
                    <FaPlus /> Add Provision
                </motion.button>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
                <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full sm:w-80 group">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Search terms..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white focus:border-primary/50 outline-none transition-all"
                        />
                    </div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                        Total Clauses: {filteredData.length}
                    </p>
                </div>

                <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-white/[0.02]">
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Clause Title</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Legal Narrative</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {currentItems.map((term, index) => (
                                <tr key={term._id} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="px-6 py-4 align-top">
                                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">{term.title}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-xs text-gray-400 line-clamp-3 leading-relaxed max-w-2xl italic">"{term.description}"</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => handleOpen(term)} className="p-2.5 rounded-xl bg-green-500/10 text-green-400 hover:bg-green-500 hover:text-white transition-all border border-green-500/20 shadow-lg shadow-green-500/5"><RiEdit2Fill size={16} /></button>
                                            <button onClick={() => { setTermsData(term); setDelOpen(true); }} className="p-2.5 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all border border-red-500/20 shadow-lg shadow-red-500/5"><RiDeleteBin6Fill size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="p-6 border-t border-white/5 flex justify-end">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, page) => setCurrentPage(page)}
                            sx={{
                                "& .MuiPaginationItem-root": { color: "#9ca3af", borderColor: "rgba(255,255,255,0.1)", borderRadius: "12px" },
                                "& .Mui-selected": { backgroundColor: "rgba(98,99,237,0.2) !important", color: "#6263ED !important", borderColor: "rgba(98,99,237,0.5) !important" }
                            }}
                        />
                    </div>
                )}
            </div>

            <Modal open={createopen} onClose={handleCreateClose} className="flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#141414] border border-white/10 rounded-[40px] w-full max-w-2xl shadow-2xl outline-none overflow-hidden">
                    <div className="p-8 border-b border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shadow-lg shadow-primary/5">
                                <FaGavel size={20} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">{TermsData ? "Edit Agreement" : "New Provision"}</h2>
                                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Legal Architecture</p>
                            </div>
                        </div>
                        <button onClick={handleCreateClose} className="p-3 rounded-2xl bg-white/5 text-gray-400 hover:text-white transition-all border border-white/10">
                            <FaTimes size={20} />
                        </button>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-gray-500 tracking-widest ml-1">Provision Title</label>
                            <input name="title" type="text" placeholder="Intellectual Property Rights" className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-primary outline-none transition-all" {...formik.getFieldProps('title')} />
                            {formik.touched.title && formik.errors.title && <p className="text-red-400 text-[10px] ml-1">{formik.errors.title}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-gray-500 tracking-widest ml-1">Legal Narrative</label>
                            <textarea name="description" rows="8" placeholder="Detail the specific terms and conditions..." className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-primary outline-none transition-all resize-none" {...formik.getFieldProps('description')} />
                            {formik.touched.description && formik.errors.description && <p className="text-red-400 text-[10px] ml-1">{formik.errors.description}</p>}
                        </div>

                        <div className="pt-4 flex gap-3">
                            <button type="button" onClick={handleCreateClose} className="flex-1 py-4 rounded-[20px] border border-white/10 text-white font-bold hover:bg-white/5">Cancel</button>
                            <button type="submit" className="flex-[2] py-4 rounded-[20px] bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all">
                                {TermsData ? 'Update Agreement' : 'Publish Provision'}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </Modal>

            <Modal open={delOpen} onClose={() => setDelOpen(false)} className="flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#141414] border border-white/10 rounded-[32px] p-10 text-center w-full max-w-sm shadow-2xl">
                    <div className="w-20 h-20 rounded-3xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-8 text-3xl border border-red-500/20">
                        <RiDeleteBin6Fill />
                    </div>
                    <h2 className="text-2xl font-bold mb-3 text-white">Discard Provision?</h2>
                    <p className="text-gray-400 text-sm mb-10 leading-relaxed">Are you sure you want to remove <span className="text-white font-bold">"{TermsData?.title}"</span>? This will be immediately purged from all legal agreements.</p>
                    <div className="flex gap-4">
                        <button onClick={() => setDelOpen(false)} className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-white font-bold">Cancel</button>
                        <button onClick={() => { dispatch(deleteTerms({ _id: TermsData._id })); setDelOpen(false); }} className="flex-1 py-4 rounded-2xl bg-red-500 text-white font-bold shadow-[0_0_30px_rgba(239,68,68,0.3)]">Delete</button>
                    </div>
                </motion.div>
            </Modal>
        </motion.div>
    )
}